import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import { Layers, ChevronDown, ChevronRight, FlaskConical, Pencil, Plus, Trash2, X, Check } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'

const rand = (n) => `R${(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export default function TreatmentMacros({ macros = [], stats = {} }) {
  const [open, setOpen] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)

  const startEdit = (m) => {
    setOpen(m.id)
    setEditingId(m.id)
    setDraft({
      name: m.name,
      laboratory: !!m.laboratory,
      items: m.items.map((i) => ({
        tariff_code: i.tariff_code || '',
        description: i.description || '',
        quantity: i.quantity ?? 1,
        visit_offset: i.visit_offset ?? 0,
      })),
    })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setDraft(null)
  }

  const setLine = (idx, patch) =>
    setDraft((d) => ({ ...d, items: d.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }))
  const addLine = () =>
    setDraft((d) => ({ ...d, items: [...d.items, { tariff_code: '', description: '', quantity: 1, visit_offset: 0 }] }))
  const removeLine = (idx) =>
    setDraft((d) => ({ ...d, items: d.items.filter((_, i) => i !== idx) }))

  const save = (m) => {
    setSaving(true)
    router.patch(
      `/treatment-macros/${m.id}`,
      {
        macro: {
          name: draft.name,
          laboratory: draft.laboratory,
          items: draft.items
            .filter((i) => i.tariff_code.trim())
            .map((i) => ({
              tariff_code: i.tariff_code.trim(),
              quantity: i.quantity,
              more_info: i.description,
              visit_offset: i.visit_offset ?? 0,
            })),
        },
      },
      {
        preserveScroll: true,
        onSuccess: () => cancelEdit(),
        onFinish: () => setSaving(false),
      }
    )
  }

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary">
          <Layers size={18} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">Treatment Macros</h1>
          <p className="text-sm text-brand-muted">
            {stats.total ?? macros.length} bundles · {stats.lines ?? '—'} lines — one click charts a whole procedure
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {macros.map((m) => {
          const isOpen = open === m.id
          const isEditing = editingId === m.id
          return (
            <div key={m.id} className="overflow-hidden rounded-xl border border-brand-border bg-white">
              <div className="flex w-full items-center gap-3 px-4 py-3">
                <button
                  onClick={() => setOpen(isOpen ? null : m.id)}
                  className="flex flex-1 items-center gap-3 text-left"
                >
                  {isOpen ? <ChevronDown size={16} className="text-brand-muted" /> : <ChevronRight size={16} className="text-brand-muted" />}
                  <span className="font-mono text-sm font-semibold text-brand-primary">{m.access_code}</span>
                  <span className="text-sm text-brand-ink">{m.name}</span>
                  {m.laboratory && (
                    <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                      <FlaskConical size={11} /> Lab
                    </span>
                  )}
                  <span className="ml-auto text-xs text-brand-muted">{m.line_count} lines</span>
                  <span className="w-24 text-right text-sm font-medium text-brand-ink">{rand(m.estimated_total)}</span>
                </button>
                {!isEditing && (
                  <button
                    onClick={() => startEdit(m)}
                    className="inline-flex items-center gap-1 rounded-md border border-brand-border px-2 py-1 text-xs font-medium text-brand-muted hover:bg-brand-surface hover:text-brand-ink"
                    title="Edit macro"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                )}
              </div>

              {isOpen && !isEditing && (
                <div className="border-t border-brand-border bg-brand-surface/30 px-4 py-2">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wide text-brand-muted">
                      <tr>
                        <th className="py-1.5 font-semibold">Code</th>
                        <th className="py-1.5 font-semibold">Description</th>
                        <th className="py-1.5 text-center font-semibold">Visit</th>
                        <th className="py-1.5 text-center font-semibold">Qty</th>
                        <th className="py-1.5 text-right font-semibold">Line total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.items.map((i, idx) => (
                        <tr key={idx} className="border-t border-brand-border/40">
                          <td className="py-1.5 font-mono text-brand-ink">{i.tariff_code}</td>
                          <td className="py-1.5 text-brand-muted">{i.description || '—'}</td>
                          <td className="py-1.5 text-center text-brand-muted">{(i.visit_offset ?? 0) + 1}</td>
                          <td className="py-1.5 text-center text-brand-ink">{i.quantity}</td>
                          <td className="py-1.5 text-right text-brand-ink">{rand(i.line_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {isEditing && draft && (
                <div className="border-t border-brand-border bg-brand-surface/30 px-4 py-3">
                  <div className="mb-3 flex flex-wrap items-center gap-3">
                    <label className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Name</label>
                    <input
                      value={draft.name}
                      onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                      className="min-w-[220px] flex-1 rounded-md border border-brand-border px-2 py-1 text-sm text-brand-ink"
                    />
                    <label className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-muted">
                      <input
                        type="checkbox"
                        checked={draft.laboratory}
                        onChange={(e) => setDraft((d) => ({ ...d, laboratory: e.target.checked }))}
                      />
                      Lab case
                    </label>
                  </div>

                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wide text-brand-muted">
                      <tr>
                        <th className="py-1.5 font-semibold">Code</th>
                        <th className="py-1.5 font-semibold">Description</th>
                        <th className="py-1.5 text-center font-semibold">Visit</th>
                        <th className="py-1.5 text-center font-semibold">Qty</th>
                        <th className="py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {draft.items.map((it, idx) => (
                        <tr key={idx} className="border-t border-brand-border/40">
                          <td className="py-1 pr-2">
                            <input
                              value={it.tariff_code}
                              onChange={(e) => setLine(idx, { tariff_code: e.target.value })}
                              placeholder="8101"
                              className="w-24 rounded-md border border-brand-border px-2 py-1 font-mono text-brand-ink"
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <input
                              value={it.description}
                              onChange={(e) => setLine(idx, { description: e.target.value })}
                              placeholder="Description (auto-fills from a known code)"
                              className="w-full rounded-md border border-brand-border px-2 py-1 text-brand-ink"
                            />
                          </td>
                          <td className="py-1 px-2 text-center">
                            <input
                              type="number"
                              min="1"
                              value={(it.visit_offset ?? 0) + 1}
                              onChange={(e) => setLine(idx, { visit_offset: Math.max(0, (parseInt(e.target.value, 10) || 1) - 1) })}
                              title="Which visit of the treatment this code belongs to (1 = first appointment)"
                              className="w-14 rounded-md border border-brand-border px-2 py-1 text-center text-brand-ink"
                            />
                          </td>
                          <td className="py-1 px-2 text-center">
                            <input
                              type="number"
                              min="1"
                              value={it.quantity}
                              onChange={(e) => setLine(idx, { quantity: e.target.value })}
                              className="w-16 rounded-md border border-brand-border px-2 py-1 text-center text-brand-ink"
                            />
                          </td>
                          <td className="py-1 text-right">
                            <button
                              onClick={() => removeLine(idx)}
                              className="inline-flex items-center rounded-md p-1 text-brand-muted hover:bg-red-50 hover:text-red-600"
                              title="Remove line"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="mt-3 flex items-center justify-between">
                    <button
                      onClick={addLine}
                      className="inline-flex items-center gap-1 rounded-md border border-dashed border-brand-border px-2 py-1 text-xs font-medium text-brand-muted hover:bg-brand-surface hover:text-brand-ink"
                    >
                      <Plus size={12} /> Add line
                    </button>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={cancelEdit}
                        disabled={saving}
                        className="inline-flex items-center gap-1 rounded-md border border-brand-border px-3 py-1.5 text-xs font-medium text-brand-muted hover:bg-brand-surface"
                      >
                        <X size={13} /> Cancel
                      </button>
                      <button
                        onClick={() => save(m)}
                        disabled={saving}
                        className="inline-flex items-center gap-1 rounded-md bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60"
                      >
                        <Check size={13} /> {saving ? 'Saving…' : 'Save changes'}
                      </button>
                    </div>
                  </div>
                  <p className="mt-2 text-[11px] text-brand-muted">
                    A recognised tariff code pulls its own catalogue description and fee — the line total refreshes after saving.
                  </p>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </DashboardLayout>
  )
}
